import { InputField } from "@/components/input-field/InputField";
import { CustomPopover } from "@/components/popover/CustomPopover";
import { SelectComponent } from "@/components/select/Select";
import { AppColors } from "@/styles/colors";
import { numbersWithCommaDecimal } from "@/utils/reg-expressions";
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  GridItem,
  HStack,
  Text,
} from "@chakra-ui/react";
import { Filter } from "lucide-react";
import { Dispatch, SetStateAction, useEffect, useState } from "react";
import { Control, FieldValues, UseFormHandleSubmit } from "react-hook-form";
import { fetchZonalGrossMarginOptionsHandler } from "@/pages/production-abm/zonal-gross-margin/schema/options-handler-functions";

interface Props {
  handleSubmit: UseFormHandleSubmit<FieldValues>;
  openFilters: boolean;
  setOpenFilters: Dispatch<SetStateAction<boolean>>;
  submitFilters: (event: FieldValues) => void;
  clearFilters: () => void;
  control: Control<FieldValues>;
  disableFilters?: boolean;
  loadingFilters?: boolean;
  filtersApplied?: boolean;
  disableClear?: boolean;
  disableApply?: boolean;
}

export const ZonalGrossMarginFilter = ({
  handleSubmit,
  openFilters,
  setOpenFilters,
  submitFilters,
  clearFilters,
  control,
  disableFilters,
  loadingFilters,
  filtersApplied,
  disableClear,
  disableApply,
}: Props) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(true);
  }, []);

  const [loadingOptions, setLoadingOptions] = useState(false);
  const [granos, setGranos] = useState<{ label: string; value: string }[]>([]);
  const [zonas, setZonas] = useState<{ label: string; value: string }[]>([]);
  const [campanas, setCampanas] = useState<{ label: string; value: string }[]>([]);

  const fetchOptions = async () => {
    setLoadingOptions(true);
    try {
      const options = await fetchZonalGrossMarginOptionsHandler();
      setGranos(options.grains.map((g) => ({ label: g.nombre, value: String(g.id) })));
      setZonas(options.zones.map((z) => ({ label: z.nombre, value: String(z.id) })));
      setCampanas(options.campaigns.map((c) => ({ label: c.nombre, value: String(c.id) })));
    } catch (error) {
      console.error("Error fetching options:", error);
    } finally {
      setLoadingOptions(false);
    }
  };

  useEffect(() => {
    if (isActive) {
      fetchOptions();
    }
  }, [isActive]);

  return (
    <Flex width="100%" paddingY="10px" justifyContent="flex-end">
      <HStack gap="10">
        <CustomPopover
          open={openFilters}
          onOpenChange={(e) => setOpenFilters(e.open)}
          triggerComponent={
            <Box position="relative" display="inline-block">
              <Button
                variant="outline"
                paddingX="12px"
                height="32px"
                disabled={disableFilters || loadingFilters}
              >
                <Filter size={16} />
                <Text fontWeight={600} fontSize="14px">
                  Filtrar
                </Text>
              </Button>
              {filtersApplied && (
                <Badge
                  position="absolute"
                  top="-4px"
                  right="-4px"
                  bg="red.500"
                  color="white"
                  fontSize="10px"
                  borderRadius="full"
                  px="2"
                  py="0.5"
                >
                  !
                </Badge>
              )}
            </Box>
          }
          bodyComponent={
            <Flex
              as="form"
              flexDir="column"
              padding="25px"
              gap="15px"
              onSubmit={handleSubmit(submitFilters)}
            >
              <Flex alignItems="center" justifyContent="space-between">
                <Text fontWeight={600} fontSize="20px">
                  Filtrar
                </Text>

                <Button
                  type="button"
                  variant="plain"
                  color={AppColors.BLUE_TEXT}
                  fontWeight={600}
                  onClick={clearFilters}
                  disabled={loadingFilters || disableClear || disableFilters}
                  loading={loadingFilters}
                >
                  Limpiar filtros
                </Button>
              </Flex>

              <Grid templateColumns="repeat(2, 1fr)" gap={4}>
                <GridItem>
                  <SelectComponent
                    name="GranoProductivo"
                    items={granos}
                    multiple
                    closeOnSelect={false}
                    labelText="Grano productivo"
                    placeholder="Seleccionar"
                    control={control}
                    clearable
                    loading={loadingOptions}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="ZonaProductiva"
                    items={zonas}
                    multiple
                    closeOnSelect={false}
                    labelText="Zona productiva"
                    placeholder="Seleccionar"
                    control={control}
                    clearable
                    loading={loadingOptions}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                  />
                </GridItem>

                <GridItem>
                  <SelectComponent
                    name="CampanaProductiva"
                    items={campanas}
                    multiple
                    closeOnSelect={false}
                    labelText="Campaña productiva"
                    placeholder="Seleccionar"
                    control={control}
                    clearable
                    loading={loadingOptions}
                    disabled={disableFilters || loadingFilters}
                    menuMaxHeight="250px"
                    minHeight="42px"
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="Equilibrio"
                    labelText="Equilibrio (qq)"
                    placeholder="Ingresar valor"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={numbersWithCommaDecimal}
                  />
                </GridItem>

                <GridItem>
                  <InputField
                    inputName="RindeEstimado"
                    labelText="Rinde estimado (qq)"
                    placeholder="Ingresar valor"
                    control={control}
                    disabled={disableFilters || loadingFilters}
                    allowedPattern={numbersWithCommaDecimal}
                  />
                </GridItem>
              </Grid>

              <Button
                bgColor={AppColors.GREEN_PRIMARY}
                type="submit"
                loading={loadingFilters}
                disabled={loadingFilters || disableFilters || disableApply}
                data-testid="zonal-gross-margin-apply-filters-btn"
              >
                Aplicar filtros
              </Button>
            </Flex>
          }
        />
      </HStack>
    </Flex>
  );
};
